import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DirectMsgComponent } from '@components/account/direct-msg/direct-msg.component';
import { InviteDialogComponent } from '@components/account/invite-dialog/invite-dialog.component';
import { RevokeInviteDialogComponent } from '@components/account/revoke-invite-dialog/revoke-invite-dialog.component';
import { Invite } from '@models/Core/Invite';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '@services/config.service';
import { SessionSocketService } from '@services/sockets/session.socket.service';
import { UserInviteService } from '@services/users/invite.service';

import { compareParticipants, ParticipantEntry } from '../../session.util';
import { ParticipantSorting } from './participant-bar/participant-bar-control/participant-bar-control.component';

@Component({
  selector: 'app-people-module',
  templateUrl: './people-module.component.html',
  styleUrls: ['./people-module.component.scss'],
})
export class PeopleModuleComponent implements OnInit, OnDestroy {
  @Input() session: Session;
  @Output() openIdea = new EventEmitter<Idea>();

  search = '';
  evaluatorInvites: Invite[] = [];
  participantInvites: Invite[] = [];
  moderatorInvites: Invite[] = [];

  sorting: ParticipantSorting = {
    field: 'role',
    dir: 'asc',
  };

  constructor(
    private config: ConfigService,
    private modal: NgbModal,
    private inviteService: UserInviteService,
    private socket: SessionSocketService,
  ) {}

  ngOnInit() {
    this.updateInvites();
    this.socket.syncInvites(this.session, {
      invite: invite => this.receiveInvite(invite, true),
      revoke: invite => this.receiveRevokation(invite),
      update: invite => this.receiveInviteUpdate(invite),
      apply: (invite, participant) =>
        this.receiveInviteApplication(invite, participant),
    });
  }

  ngOnDestroy() {
    this.socket.unsyncInvites();
  }

  receiveInviteApplication(invite: Invite, participant: Participant) {
    this.receiveRevokation(invite);
    this.session.config.participants.push(participant);
  }

  receiveInviteUpdate(invite: Invite) {
    this.moderatorInvites.forEach(i => {
      if (i.email === invite.email) {
        i.sessions = invite.sessions;
      }
    });

    this.participantInvites.forEach(i => {
      if (i.email === invite.email) {
        i.sessions = invite.sessions;
      }
    });

    this.evaluatorInvites.forEach(i => {
      if (i.email === invite.email) {
        i.sessions = invite.sessions;
      }
    });
  }

  receiveRevokation(invite: Invite) {
    this.moderatorInvites = this.moderatorInvites.filter(
      i => i.email !== invite.email,
    );
    this.participantInvites = this.participantInvites.filter(
      i => i.email !== invite.email,
    );
    this.evaluatorInvites = this.evaluatorInvites.filter(
      i => i.email !== invite.email,
    );
  }

  receiveInvite(invite: Invite, revoke = false) {
    if (revoke) {
      this.receiveRevokation(invite);
    }

    const row = invite.sessions.find(
      s => s.session.toString() === this.session._id.toString(),
    );
    if (row.role === this.config.roles.SESSION_ADMIN) {
      this.moderatorInvites.push(invite);
    } else if (row.role === this.config.roles.SESSION_MODERATOR) {
      this.moderatorInvites.push(invite);
    } else if (row.role === this.config.roles.SESSION_PARTICIPANT) {
      this.participantInvites.push(invite);
    } else if (row.role === this.config.roles.SESSION_EXTERNAL_EVALUATOR) {
      this.evaluatorInvites.push(invite);
    }
  }

  updateInvites() {
    this.inviteService.session(this.session).subscribe(invites => {
      this.moderatorInvites = [];
      this.participantInvites = [];
      this.evaluatorInvites = [];

      invites.forEach(invite => this.receiveInvite(invite));
    });
  }

  get entries(): ParticipantEntry[] {
    return [
      ...this.members.map(
        member =>
          ({
            type: 'participant',
            entry: member,
          }) as ParticipantEntry,
      ),
      ...this.moderatorInvites.map(
        i => ({ type: 'invite', entry: i }) as ParticipantEntry,
      ),
      ...this.participantInvites.map(
        i => ({ type: 'invite', entry: i }) as ParticipantEntry,
      ),
      ...this.evaluatorInvites.map(
        i => ({ type: 'invite', entry: i }) as ParticipantEntry,
      ),
    ];
  }

  get sortedEntries() {
    return this.entries.sort((a, b) =>
      compareParticipants(a, b, this.sorting, this.session),
    );
  }

  get members() {
    return this.session.config.participants
      .filter(p => !!p.user)
      .filter(p => p.user.registered)
      .filter(p => p.user.email !== 'vgamper@gmail.com')
      .filter(
        p =>
          (
            (p.user.first_name || '') +
            ' ' +
            p.user.name +
            ' (' +
            (p.user.organization || '') +
            ')'
          )
            .toLowerCase()
            .indexOf(this.search.toLowerCase()) !== -1,
      );
  }

  get moderators() {
    return this.members
      .filter(
        p =>
          p.role === this.config.roles.SESSION_ADMIN ||
          p.role === this.config.roles.SESSION_MODERATOR,
      )
      .map(p => p.user);
  }

  get participants() {
    return this.members
      .filter(p => p.role === this.config.roles.SESSION_PARTICIPANT)
      .map(p => p.user);
  }

  get externals() {
    return this.members
      .filter(p => p.role === this.config.roles.SESSION_EXTERNAL_EVALUATOR)
      .map(p => p.user);
  }

  directMsg(user) {
    const ref = this.modal.open(DirectMsgComponent);
    ref.componentInstance.user = user;
  }

  get canInvite() {
    return this.session.me.can_invite;
  }

  isCreator(invite: Invite) {
    return invite.created_by === this.session.me.user_id;
  }

  invite() {
    this.modal
      .open(InviteDialogComponent, {
        size: 'lg',
        beforeDismiss: () => {
          return new Promise((resolve, reject) => {
            if (!confirm('Are you sure you want to discard this invitation?')) {
              reject();
            } else {
              resolve(true);
            }
          });
        },
      })
      .result.then(changed => {
        if (changed) {
          this.updateInvites();
        }
      });
  }

  uninvite(invite: Invite) {
    const ref = this.modal.open(RevokeInviteDialogComponent, {
      size: 'lg',
    });
    ref.componentInstance.invite = {
      ...invite,
      sessions: [...invite.sessions],
    };
    ref.result.then(changed => {
      if (changed) {
        this.updateInvites();
      }
    });
  }

  canUninvite(invite: Invite) {
    const row = invite.sessions.find(
      r => r.session.toString() === this.session._id.toString(),
    );
    return this.session.me.role <= row.role;
  }

  trackById(i: number, entry: ParticipantEntry) {
    return entry.entry._id;
  }
}
